import { TopoNode } from "../Topology/TopoNode";
import { List } from "../Utils/List";
import { Dictionary } from "../Utils/Dictionary";
import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { TopoMap } from "../Topology/TopoMap";

export class BFS {
	private _edgeTo: Dictionary<TopoNode, TopoNode>;
	private _distTo: Dictionary<TopoNode, number>;

	constructor(map: TopoMap, root: TopoNode) {
		this.Search(map, root);
	}

	private Search(map: TopoMap, root: TopoNode): void {
		var q = new Array<TopoNode>();
		this._distTo = new Dictionary<TopoNode, number>();
		this._edgeTo = new Dictionary<TopoNode, TopoNode>();

		this._distTo.Add(root, 0);
		q.push(root);

		while (q.length > 0) {
			var node: TopoNode = q.pop();

			map.AdjacentNodes(node).ForEach((neighbor) => {
				if (!this._distTo.ContainsKey(neighbor)) {
					this._edgeTo.Add(neighbor, node);
					this._distTo.Add(neighbor, this._distTo.Item(node) + 1);
					q.push(neighbor);
				}
			});
		}
	}

	public get PathEdges(): Dictionary<TopoNode, List<TopoNode>> {
		var result = new Dictionary<TopoNode, List<TopoNode>>();

		this._edgeTo.ForEach((entry) => {
			if (!result.ContainsKey(entry.Key))
				result.Add(entry.Key, new List<TopoNode>());

			if (!result.ContainsKey(entry.Value))
				result.Add(entry.Value, new List<TopoNode>());

			result.Item(entry.Value).Add(entry.Key);
		});

		return result;
	}

	public get ParentEdges(): Dictionary<TopoNode, TopoNode> {
		return this._edgeTo;
	}

	public PathTo(node: TopoNode): List<TopoNode> {
		if (!this._distTo.ContainsKey(node)) return new List<TopoNode>();

		var path = new List<TopoNode>();

		var x: TopoNode= null;
		for (x = node; this._distTo.Item(x) != 0; x = this._edgeTo.Item(x)) {
			path.Add(x);
		}

		path.Add(x);

		return path;
	}

	public FurthestNode(): TopoNode {
		var maxDepth = 0;
		var deepestNode: TopoNode = null;

		this._distTo.ForEach((pair) => {
			var depth: number = pair.Value;
			if (depth > maxDepth) {
				maxDepth = depth;
				deepestNode = pair.Key;
			}
		});

		if (deepestNode == null && this._distTo.Count > 0)
			deepestNode = this._distTo.First().Key;

		return deepestNode;
	}
}

export class NodeContainer {
	public Left: number;
	public Right: number;
	public Top: number;
	public Bottom: number;
	public get Width(): number {
		return Math.abs(this.Right - this.Left);
	}

	public get Height(): number {
		return Math.abs(this.Bottom - this.Top);
	}

	public get CenterX(): number {
		return this.Left + (this.Width / 2);
	}

	public get CenterY(): number {
		return this.Top + (this.Height / 2);
	}

	public CalculateSize(nodes: Iterable<TopoNode>): void {
		this.Left = Number.MAX_VALUE
		this.Right = Number.MIN_VALUE;
		this.Top = Number.MAX_VALUE;
		this.Bottom = Number.MIN_VALUE;

		nodes.ForEach((node) => {
			if (node.Bottom > this.Bottom)
				this.Bottom = node.Bottom;

			if (node.Top < this.Top)
				this.Top = node.Top;

			if (node.Right > this.Right)
				this.Right = node.Right;

			if (node.Left < this.Left)
				this.Left = node.Left;
		});

		if (this.Left == Number.MAX_VALUE)
			this.Left = 0;
		if (this.Top == Number.MAX_VALUE)
			this.Top = 0;
		if (this.Right == Number.MIN_VALUE)
			this.Right = 0;
		if (this.Bottom == Number.MIN_VALUE)
			this.Bottom = 0;
	}
}

export class SpanningTree extends NodeContainer
{
	private _root: TopoNode;
	private _edges: Dictionary<TopoNode, List<TopoNode>>;
	private _parents: Dictionary<TopoNode, TopoNode> ;
	private _map: TopoMap;

	constructor(map: TopoMap, root: TopoNode) {
		super();
		this._map = map;
		this.Root = root;
	}

	public get Root(): TopoNode {
		return this._root;
	}
	public set Root(value: TopoNode) {
		this._root = value;
		this.CalculateSpanningTree();
	}

	public get Nodes(): List<TopoNode> {
		return this._edges.Keys();
	} 

	public Offset(xOffset:number, yOffset:number): void {
		this.Nodes.ForEach((node) => {
			node.Offset(xOffset, yOffset);
		});
	}

	private CalculateSpanningTree(): void {
		if (!!this._root) {
			var bfs = new BFS(this._map, this._root);
			this._edges = bfs.PathEdges;
			this._parents = bfs.ParentEdges;
		}
		else
			this._edges = new Dictionary<TopoNode, List<TopoNode>>();
	}

	public PickRoot():void {
		var nodes: List<TopoNode> = this.Nodes;

		if (nodes.Count > 0) {
			// Root is chosen by finding the longest path and choosing
			// the node in the middle in an attempt to make a balanced tree

			// Find Diameter
			// Choose node and find furthest node a
			// Find furthest node b from a
			// diameter is path between b and a
			var random: TopoNode = nodes.Get(0);
			var bfs = new BFS(this._map, random);
			var outerA: TopoNode = bfs.FurthestNode();
			bfs = new BFS(this._map, outerA);
			var outerB: TopoNode = bfs.FurthestNode();

			var path = bfs.PathTo(outerB);

			// Root is in the middle
			this.Root = path.Get(Math.floor(path.Count / 2));
		}
	}

	public Contains(node: TopoNode):boolean {
		return this._edges.ContainsKey(node);
	}

	public ChildNodes(node: TopoNode): List<TopoNode> 
	{
		if (this.Contains(node))
			return this._edges.Item(node);
		else
			return new List<TopoNode>();
	}

	public DescendantNodes(node: TopoNode): List<TopoNode> {
		var result = new List<TopoNode>();

		this.ChildNodes(node).ForEach((child) => {
			result.UnionWith(this.DescendantNodes(child));
			result.Add(child);
		});

		return result;
	}

	public HasDescendants(node: TopoNode):boolean {
		return this.ChildNodes(node).Count > 0;
	}

	public HasParent(node:TopoNode):boolean {
		return this.Parent(node) != null;
	}

	public Parent(node: TopoNode): TopoNode {
		return this._parents.Item(node);
	}

	public CalculateSize(): void {
		super.CalculateSize(this._edges.Keys());
	}
}
